import bowser from 'bowser'
import LocalStorageManager from './localStorageManager.js'
import APIManager from './apiManager.js'
import { buildNumber, version } from '../constants/buildNumber.json'
import { getJid } from '../util/utilitys.js'

const localStorageManager = LocalStorageManager.getInstance()

function supportUnifiedPlan() {
    return (
        (bowser.firefox && bowser.version >= 38) ||
        (bowser.chrome && bowser.version >= 73) ||
        (bowser.safari && bowser.version >= 12.0)
    )
}

export default class PhoneLogManager {
    constructor({ pc, groupID, getMotionStatus }) {
        this.pc = pc
        this.startTime = null
        this.fps = 0
        this.timezone = (-1 * new Date().getTimezoneOffset()) / 60
        this.tz = Intl.DateTimeFormat().resolvedOptions().timeZone
        this.getMotionStatus = getMotionStatus
        this.groupID = groupID

        const email = localStorageManager.cameraSettings.email
        const deviceId = localStorageManager.deviceSettings.deviceId
        const { jid, deviceName } = getJid({ email, deviceId })
        this.profile = {
            login_type: 'Google',
            device: 'WIFI',
            networkType: 'wifi',
            detail: deviceName,
            app: {
                version,
                build: buildNumber,
            },
            appversion: buildNumber,
            user: email,
            jid,
            camera: 'true',
            type: 'camera',
        }

        this.sendPhoneLogInterval = null
        this.updatePhoneLogInterval = null
        this.apiManager = APIManager.getInstance()
        this.candidatesInfo = {
            local: [],
            remote: [],
        }
        this.candidateNominated = {}
        this.candidateSelected = {}
    }

    startMonitor() {
        this.startTime = new Date()

        this.phoneLog = {
            groupID: this.groupID,
            profile: this.profile,
            fps: this.fps,
            timezone: this.timezone,
            tz: this.tz,
            hw_encoder: 0,
            owner: localStorageManager.cameraSettings.email,
            log: [
                {
                    startTime: new Date().toISOString(),
                },
            ],
            conn_stats: {
                video: { jitter: null, packets: { sent: null, lost: null } },
                audio: { jitter: null, packets: { sent: null, lost: null } },
            },
        }
        this.candidatesInfo = {
            local: [],
            remote: [],
        }
        this.candidateNominated = {}
        this.candidateSelected = {}

        let prevFrameEncoded = 0
        this.updatePhoneLogInterval = setInterval(async () => {
            const results = await this.pc.getStats(null)
            // figure out the peer's ip
            let activeCandidatePair = null
            let selectedCandidatePairId = null
            results.forEach((report) => {
                const connStats = this.phoneLog.conn_stats
                switch (report.type) {
                    case 'outbound-rtp':
                        switch (report.mediaType) {
                            case 'video':
                                this.fps =
                                    report.framesEncoded - prevFrameEncoded
                                prevFrameEncoded = report.framesEncoded
                            case 'audio':
                                connStats[
                                    report.mediaType
                                ].packets.sent = Math.max(
                                    connStats[report.mediaType].packets.sent,
                                    report.packetsSent
                                )
                                break
                            default:
                                break
                        }
                        break
                    case 'remote-inbound-rtp':
                        switch (report.kind) {
                            case 'video':
                            case 'audio':
                                connStats[report.kind].packets.lost = Math.max(
                                    connStats[report.kind].packets.lost,
                                    report.packetsLost
                                )
                                connStats[report.kind].jitter = Math.max(
                                    connStats[report.kind].jitter,
                                    report.jitter
                                )
                                break
                            default:
                                break
                        }
                        break
                    case 'transport':
                        selectedCandidatePairId = report.selectedCandidatePairId
                        // Search for the candidate pair, spec-way first.
                        activeCandidatePair = results.get(
                            report.selectedCandidatePairId
                        )
                        break
                    case 'local-candidate':
                        if (
                            this.candidatesInfo.local.every(
                                (c) => c.id !== report.id
                            )
                        ) {
                            this.candidatesInfo.local.push(report)
                        }
                        break
                    case 'remote-candidate':
                        if (
                            this.candidatesInfo.remote.every(
                                (c) => c.id !== report.id
                            )
                        ) {
                            this.candidatesInfo.remote.push(report)
                        }
                        break
                    case 'candidate-pair':
                        if (report.nominated && report.state === 'succeeded') {
                            const local = this.candidatesInfo.local.find(
                                (ele) => ele.id === report.localCandidateId
                            )
                            const remote = this.candidatesInfo.remote.find(
                                (ele) => ele.id === report.remoteCandidateId
                            )
                            if (local && remote) {
                                this.onCandidateNominated(
                                    selectedCandidatePairId,
                                    local,
                                    remote
                                )
                                if (
                                    !supportUnifiedPlan() ||
                                    report.selected ||
                                    selectedCandidatePairId === report.id
                                ) {
                                    this.onCandidateSelected(
                                        selectedCandidatePairId,
                                        local,
                                        remote
                                    )
                                }
                            }
                        }
                        break
                    default:
                        break
                }
            })
            if (activeCandidatePair && activeCandidatePair.remoteCandidateId) {
                let remoteCandidate = results.get(
                    activeCandidatePair.remoteCandidateId
                )
                let localCandidate = results.get(
                    activeCandidatePair.localCandidateId
                )

                this.phoneLog.netCategory = 'remote'
                this.phoneLog.isRelay = localCandidate.type === 'relay'
            }
        }, 1000)

        this.sendPhoneLogInterval = setInterval(() => {
            this.phoneLog.log.push({
                endTime: new Date().toISOString(),
            })

            this.sendPhoneLog({})
        }, 300000)
    }

    getCandidateInfo(local, remote) {
        return [
            'candidate, ',
            'local: ',
            local.ip || local.ipAddress,
            ':',
            local.port || local.portNumber,
            ' ',
            local.protocol || local.transport || local.mozLocalTransport,
            ' ',
            local.candidateType,
            ' - ',
            'remote: ',
            remote.ip || remote.ipAddress,
            ':',
            remote.port || remote.portNumber,
            ' ',
            remote.protocol || remote.transport || remote.mozLocalTransport,
            ' ',
            remote.candidateType,
        ].join('')
    }

    onCandidateNominated(id, local, remote) {
        if (!this.candidateNominated[id]) {
            this.phoneLog.log.push({
                name: 'onCandidateNominated',
                date: new Date().toISOString(),
                type: 'event',
                content: this.getCandidateInfo(local, remote),
            })
            this.candidateNominated[id] = true
        }
    }

    onCandidateSelected(id, local, remote) {
        if (!this.candidateSelected[id]) {
            this.phoneLog.log.push({
                name: 'onCandidateSelected',
                date: new Date().toISOString(),
                type: 'event',
                content: this.getCandidateInfo(local, remote),
            })
            this.candidateSelected[id] = true
        }
    }

    async sendPhoneLog({ extraLog }) {
        const motionStatus = this.getMotionStatus()
        const phoneLog = {
            ...this.phoneLog,
            ...extraLog,
            motionDetection: motionStatus ? 'on' : 'off',
            datetime: new Date(),
            fps: this.fps,
        }

        this.phoneLog.log = [
            {
                startTime: new Date(),
            },
        ]

        await this.apiManager.reportConnectionLog(phoneLog)
    }

    endMonitor() {
        const {
            total_connection_duration: totalConnectionDuration,
            // totalPreviewDuration,
            total_connection_count: totalConnectionCount,
        } = localStorageManager.deviceSettings

        const stopTime = new Date()
        let thisConnectionDuration = 0
        if (this.startTime instanceof Date) {
            thisConnectionDuration =
                stopTime.getTime() - this.startTime.getTime()
        }
        this.phoneLog.log.push({
            endTime: new Date().toISOString(),
        })
        const extraLog = {
            this_connection_duration: thisConnectionDuration,
            total_connection_duration:
                totalConnectionDuration + thisConnectionDuration,
            total_connection_count: totalConnectionCount + 1,
        }

        localStorageManager.setSettingsCache({
            type: 'device',
            updateObj: extraLog,
        })

        clearInterval(this.sendPhoneLogInterval)
        clearInterval(this.updatePhoneLogInterval)
        this.sendPhoneLog({
            extraLog,
        })
    }
}
